import type { ColDef, GetQuickFilterTextParams, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { IAppManagementByDeviceType } from '~/types/application-management'

import AppStoreLogo from '~/assets/app-store-logo.png'
import GooglePlayStoreLogo from '~/assets/google-play-store-logo.png'
import FavoriteStar from '~/components/FavoriteStar'
import { FASTLY_PATH } from '~/const/asset'
import { resize } from '~/utils/media'
import { applicationColDef } from '~/utils/table'

import './styles.scss'

export interface IContext {
  favouriteApps: {
    app_key: string
    device_type: string
  }[]
  onSetAsFavourite: (data: IAppManagementByDeviceType) => void
}

const getStoreLogo = (deviceType: string): string => {
  if (deviceType === 'android') {
    return GooglePlayStoreLogo
  } else if (deviceType === 'ios') {
    return AppStoreLogo
  }
  return ''
}

const LinkCellRenderer = ({ data, value }: ICellRendererParams<IAppManagementByDeviceType, string>): ReactElement => {
  if (!data || !value) return <div />

  return (
    <a className="store-url" href={value} rel="noreferrer" target="_blank">
      <img src={getStoreLogo(data.device_type)} />
    </a>
  )
}

const FavouriteCellRenderer = ({
  context,
  data,
}: ICellRendererParams<IAppManagementByDeviceType, unknown, IContext>): ReactElement => {
  const { favouriteApps, onSetAsFavourite } = context

  if (data) {
    const { app_key: appKey, device_type: deviceType } = data
    const isFavourite = !!favouriteApps.find((el) => el.app_key === appKey && el.device_type === deviceType)
    const handleSetAsFavourite = () => onSetAsFavourite(data)

    return (
      <div onClick={handleSetAsFavourite}>
        <FavoriteStar isActive={isFavourite} />
      </div>
    )
  }

  return <div />
}

const columns: ColDef[] = [
  // NOTE: used by the search, DO NOT REMOVE THIS HIDDEN COLUMN
  {
    field: 'app_key',
    hide: true,
  },
  applicationColDef({
    field: 'default_display_name',
    flex: 1,
    getQuickFilterText: ({ data }: GetQuickFilterTextParams<IAppManagementByDeviceType>): string =>
      data?.default_display_name || '',
    tooltipField: 'default_display_name',
    valueGetter: ({ data }: ValueGetterParams<IAppManagementByDeviceType>): Record<string, string> | null => {
      if (!data) return null

      const { default_display_name, device_type, icon_file_id } = data

      return {
        app_icon_url: icon_file_id ? resize(`${FASTLY_PATH}${icon_file_id}`, 32, 32) : '',
        device_type,
        name: default_display_name || '',
      }
    },
  }),
  {
    cellRenderer: LinkCellRenderer,
    field: 'external_store_id',
    headerName: 'Store URL',
    resizable: false,
    suppressHeaderMenuButton: true,
    valueGetter: ({ data }: ValueGetterParams<IAppManagementByDeviceType>): string | null => {
      if (!data) return null

      const { device_type, external_store_id } = data

      if (device_type === 'android') {
        return `https://play.google.com/store/apps/details?id=${external_store_id}`
      } else if (device_type === 'ios') {
        return `https://apps.apple.com/app/id${external_store_id}`
      }

      return ''
    },
    width: 110,
  },
  {
    cellRenderer: FavouriteCellRenderer,
    field: 'favourite',
    headerName: '',
    resizable: false,
    suppressHeaderMenuButton: true,
    width: 54,
  },
]

export default columns
